<template>
  <div id="app" class="app">
    <div v-if="isIE">
      <p>
        ATTENTION: Microsoft is discontinuing support for Internet Explorer 11 in August 2021
        - please use an alternate browser to complete this form. For further assistance,
        please contact our Membership Team by phone on 0207 572 2737 or via email at membership@rcpharm.org
      </p>
    </div>
    <div v-else-if="isSuperUser">
      <p>
        Applications cannot be performed with a super user account.
      </p>
    </div>
    <div v-else-if="isMember">
      <p>
        Our records show that you are already a member.
      </p>
    </div>
    <div v-else-if="isOutstandingRenewal">
      <p>
        Our records show that your membership renewal is outstanding. Please renew at <a href="/my-rps/renew-my-membership">Renew my membership</a>
      </p>
    </div>
    <div v-else-if="isCompleted && isApplicant" class="completed">
      <p>You have already completed your application.</p>
    </div>
    <div v-else-if="setupCompleted && isLoggedInWithNoDob">
      <p>
        Your profile does not contain a date of birth. Please follow the link <a href="/my-rps/my-profile">Add Date of Birth</a>
      </p>
    </div>
    <div id="application" class="desktop-view" v-else>
      <MobileProgressBar v-if="isMobile" />
      <DesktopProgressBar v-if="!isMobile" @changeStep="goToArea" />
      <div class="view">
        <component @hook:mounted="init" ref="comp" :backButton="this.backButton" :is="currentComponent.component"
                   @completed="goForward" @saveInProgress="isSaveInProgress = $event"
                   :cancelBack="cancelBack" @resetCancelBack="cancelBack = $event" />
      </div>
    </div>
    <b-modal ref="saveProgress" :active.sync="isSaveInProgress" :can-cancel="canCancelModal" @close="resetModal">
      <b-progress v-bind:style="{display: saveResult.displayProgress}"></b-progress>
      <b-message :title="saveResult.title" :type="saveResult.messageType" @close="$refs.saveProgress.close();"
                 aria-close-label="Close message" :active.sync="resultReturned">
        <span v-html="saveResult.message"></span>
      </b-message>
    </b-modal>
  </div>
</template>
<script>
import {
  mapActions, mapGetters, mapState, mapMutations,
} from 'vuex';
import UnderstandingYou from '@/understandingyou/UnderstandingYou.vue';
import Confirmation from '@/understandingyou/Confirmation.vue';
import PersonalDetails from '@/personaldetails/PersonalDetails.vue';
import Correspondence from '@/correspondence/Correspondence.vue';
import Insurance from '@/insurance/Insurance.vue';
import '@/styles/application.scss';
import '@/mobile/mobile.scss';
import MobileProgressBar from '@/components/MobileProgressBar.vue';
import DesktopProgressBar from '@/components/DesktopProgressBar.vue';
import Payments from '@/payments/Payments.vue';
import FinalConfirmation from '@/confirmation/Confirmation.vue';
import Loading from './Loading.vue';
import Mobile from './mobile/mobile';
import Manager from './applicationManager';
import Api from './api';
import Ga from './ga';

export default {
  name: 'app',
  components: {
    UnderstandingYou,
    Confirmation,
    PersonalDetails,
    Correspondence,
    Insurance,
    MobileProgressBar,
    DesktopProgressBar,
    Payments,
    FinalConfirmation,
  },
  props: {
    dataLayer: Array,
    navBar: HTMLDivElement,
    enableBraintreeFeature: Boolean,
    enableDirectDebit: Boolean,
    clientTokenApi: String,
    logApi: String,
    paymentSubmissionApi: String,
    paymentOptionsInfo: String,
    paymentOptionsInfoInternationalStudent: String,
    devUserId: Number,
    memberCategory: String,
  },
  data() {
    return {
      innerWidth: 0,
      currentComponent: { component: Loading, AreaRegisteredTo: 'UnderstandingYou', order: 1 },
      isSuperUser: false,
      completed: false,
      backButton: null,
      isSaveInProgress: false,
      canCancelModal: false,
      resultReturned: false,
      saveResult: {
        successful: true,
        message: '',
        title: 'Saved',
        messageType: 'is-success',
        displayProgress: '',
        reloadForm: false,
      },
      componentsRegistry: [
        { component: UnderstandingYou, AreaRegisteredTo: 'UnderstandingYou', order: 1 },
        { component: Confirmation, AreaRegisteredTo: 'UnderstandingYou', order: 2 },
        { component: PersonalDetails, AreaRegisteredTo: 'PersonalDetails', order: 1 },
        { component: Correspondence, AreaRegisteredTo: 'Address', order: 1 },
        { component: Insurance, AreaRegisteredTo: 'Insurance', order: 1 },
        { component: Payments, AreaRegisteredTo: 'Payment', order: 1 },
        { component: FinalConfirmation, AreaRegisteredTo: 'Confirmation', order: 1 }],
      setupCompleted: false,
      cancelBack: false,
    };
  },
  async created() {
    this.setGaDataLayer(this.dataLayer);
    this.setBraintreeOptions({
      useFeature: this.enableBraintreeFeature,
      tokenApi: this.clientTokenApi,
      paymentApi: this.paymentSubmissionApi,
      logApi: this.logApi,
    });
    this.setPaymentOptionsInfo({
      paymentOptionsInfo: this.paymentOptionsInfo,
      paymentOptionsInfoInternationalStudent: this.paymentOptionsInfoInternationalStudent,
    });
    this.setDirectDebitEnabled(this.enableDirectDebit);
    this.setPreSelectedMembershipCategory(this.memberCategory);

    if (this.isMobile) {
      this.backButton = this.navBar.querySelector('.mobile-breadcrumbs a');
      this.navBar.classList.add('navBar');
      this.backButton.removeAttribute('href');
    }

    await this.setOptions();

    if (process.env.NODE_ENV === 'development') {
      Api.setUserId(this.devUserId);
    }

    const applicationAndState = this.isLoggedIn ? await Manager.getCurrentApplication() : {
      application: null,
      isFound: false,
      userInfo: {
        userId: 0,
        title: '',
        titelOther: '',
        email: '',
        firstname: '',
        lastname: '',
        dob: '',
        membershipCategory: '',
        userStatus: '',
        contactId: '',
      },
    };

    await this.saveApplicationToStore(applicationAndState);

    if (applicationAndState.isFound) {
      const applicationStep = applicationAndState.application.ApplicationStep;
      switch (applicationStep) {
        case 'PersonalDetails':
          this.currentComponent = this.componentsRegistry.find((o) => o.component === PersonalDetails);
          break;
        case 'Address':
          this.currentComponent = this.componentsRegistry.find((o) => o.component === Correspondence);
          break;
        case 'Insurance':
          this.currentComponent = this.componentsRegistry.find((o) => o.component === Insurance);
          break;
        case 'Payment':
          this.currentComponent = this.componentsRegistry.find((o) => o.component === Payments);
          break;
        case 'Complete':
          this.isCompleted = true;
          break;
        default:
          console.log('Sorry, this step is not existing.');
      }
    } else {
      [this.currentComponent] = this.componentsRegistry;
    }

    this.setupCompleted = true;
  },
  mounted() {
    this.isSuperUser = process.env.NODE_ENV === 'development' ? false : document.getElementById('isSuperUser').value === 'True';
    if (this.isMobile) {
      this.backButton?.addEventListener('click', this.goBack);
    }
    window.addEventListener('resize', () => {
      if (this.innerWidth !== window.innerWidth) { this.reinit(); }
    });

    if (process.env.NODE_ENV !== 'development') {
      const baseContainer = document.getElementById('base-container');
      baseContainer.style.backgroundColor = '#f6f1ed';
    }
    if (!window.sessionStorage.getItem('ApplicationInvoked')) {
      Ga.sendApplicationInvoked();
    } else { window.sessionStorage.removeItem('ApplicationInvoked'); }
  },
  destroyed() {
    if (this.isMobile) {
      this.backButton?.removeEventListener('click', this.goBack);
    }
  },
  methods: {
    ...mapActions(['saveApplicationToStore', 'setOptions']),
    ...mapMutations(['setArea', 'setCardPaymentResult', 'setGaDataLayer', 'setBraintreeOptions', 'setPaymentOptionsInfo',
      'setDirectDebitEnabled', 'setPreSelectedMembershipCategory']),
    init() {
      if (this.currentComponent.component.name === 'Loading') {
        return;
      }

      if (this.isMobile) {
        Mobile.initApplicationView();
        this.innerWidth = window.innerWidth;
      }
      this.setArea({ selectedArea: this.currentComponent.AreaRegisteredTo });
    },
    goToArea(newAreaName) {
      const newComponent = this.componentsRegistry.filter((comp) => comp.AreaRegisteredTo === newAreaName).sort((a, b) => a.order - b.order)[0];
      const { comp } = this.$refs;
      comp.$destroy();
      this.currentComponent = newComponent;
    },
    goForward(saveResponse) {
      if (this.processResponse(saveResponse)) this.updateComponent();
    },
    updateComponent() {
      const indexer = this.currentComponentIndex;
      const ukStudentSavedCorrespondence = this.isUkStudent() && this.isCorrespondence;
      const confirmationIndex = 6;

      if (indexer.indexSet) {
        this.currentComponent = this.componentsRegistry[ukStudentSavedCorrespondence ? confirmationIndex : indexer.nextIndex];

        const { comp } = this.$refs;
        comp.$destroy();
        // scroll to top (especially)after clicking Let's do this on mobile
        window.scrollTo(0, 20);
      }
    },
    processResponse(saveResponse) {
      if (saveResponse) {
        const personalDetailsNoLoggedIn = this.isPersonalDetails && !this.isLoggedIn;
        const reloadIfNotUkStudent = saveResponse.reloadForm && !this.isUkStudent();

        this.saveResult.displayProgress = 'none';
        this.resultReturned = true;
        this.saveResult.successful = saveResponse.successful;
        this.saveResult.message = saveResponse.message;

        if (!saveResponse.successful) {
          this.saveResult.title = saveResponse.title || 'Save issue';
          this.saveResult.messageType = 'is-danger';
          return false;
        }

        if (reloadIfNotUkStudent || personalDetailsNoLoggedIn) {
          this.saveResult.reloadForm = true;
          return false;
        }
      }
      return true;
    },
    goBack() {
      const { comp } = this.$refs;
      // need to cancel back button in these cases and handle it in the parent component to set the appropriate child component
      if (this.isRegulatorySection || this.isCardPaymentOrDirectDebitInfoSection) {
        this.cancelBack = true;
        return;
      }

      // First section of first area
      if (this.isFirstSection && this.componentsRegistry.indexOf(this.currentComponent) === 0) return;
      // Final Confirmation
      if (this.currentComponent.component === FinalConfirmation) return;
      // Go back to previous area
      if (this.isFirstSection) {
        const paymentOptionsIndex = 5;
        const currentIndex = this.componentsRegistry.indexOf(this.currentComponent);
        const remove = paymentOptionsIndex === currentIndex && !this.isPliCategory
          ? 2
          : 1;

        comp?.$destroy();
        this.currentComponent = this.componentsRegistry[currentIndex - remove];
        window.scrollTo(0, 20);
      } else {
        // Go back to previous section within area
        comp?.swipeHandler('right');
      }
    },
    resetModal() {
      if (this.saveResult.reloadForm) {
        let url = new URL(window.location);

        if (url.searchParams.has('membershipCategory')) {
          url.searchParams.delete('membershipCategory');

          url.search = url.searchParams;
          url = url.toString();

          console.log(`url is ${url}`);
          window.location = url;
        } else {
          window.location.reload();
        }
        window.sessionStorage.setItem('ApplicationInvoked', 'on');
      }
      this.saveResult.successful = true;
      this.saveResult.title = 'Saved';
      this.saveResult.messageType = 'is-success';
      this.resultReturned = false;
      this.isSaveInProgress = false;
      this.saveResult.displayProgress = 'block';
    },
    reinit() {
      if (this.isMobile) {
        Mobile.reinitSections(this.inviewElement);
        this.innerWidth = window.innerWidth;
      }
    },
    isUkStudent() {
      return !this.getIsOverseas && this.application.Type === 'Student';
    },
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'isMember', 'isApplicant', 'isOutstandingRenewal', 'isFirstSection', 'getIsOverseas', 'application',
      'isLoggedInWithNoDob', 'isPliCategory', 'currentSection']),
    ...mapState(['isMobile', 'inviewElement']),
    isIE() {
      // https://stackoverflow.com/questions/9847580/how-to-detect-safari-chrome-ie-firefox-and-opera-browser
      const result = /* @cc_on!@ */ false || !!document.documentMode;
      return result;
    },
    isPersonalDetails() {
      return this.currentComponent.component.name === 'PersonalDetails';
    },
    isCorrespondence() {
      return this.currentComponent.component.name === 'Correspondence';
    },
    isRegulatorySection() {
      return this.currentComponent.component.name === 'Insurance' && this.currentSection === 'regulatorySection';
    },
    isCardPaymentOrDirectDebitInfoSection() {
      return this.currentComponent.component.name === 'Payments'
        && (this.currentSection === 'directDebitInfo' || this.currentSection === 'card-payment');
    },
    isCompleted: {
      get() {
        return this.completed;
      },
      set(newValue) {
        this.completed = newValue;
      },
    },
    currentComponentIndex() {
      const currentComponentIndex = this.componentsRegistry.indexOf(this.currentComponent);
      const correspondenceIndex = 3;
      let nextOffset = 1;

      if (currentComponentIndex === correspondenceIndex && !this.isPliCategory) {
        nextOffset += 1;
      }

      return {
        index: currentComponentIndex,
        indexSet: currentComponentIndex < (this.componentsRegistry.length - 1) && currentComponentIndex > -1,
        nextIndex: currentComponentIndex + nextOffset,
      };
    },
  },
};
</script>
<style lang="scss">
  @import '@/styles/buefy.scss';
  @import './styles/application.scss';
  @import './mobile/mobile.scss';

  @media screen and (orientation:portrait) {
    .navBar {
      position: sticky;
      position: -webkit-sticky;
      top: 56px;
      z-index: 3;
    }
  }
  /* Landscape */
  @media screen and (orientation:landscape) {
    .navBar {
      position: sticky;
      position: -webkit-sticky;
      top: 56px;
      z-index: 3;
    }
  }
  .completed {
      text-align: center;
  }

</style>
